import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { FullPageLoading } from '../../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TableWrapper,
} from '../../../../../../CommonComponents/OPL/Table/Table';
import { TruncatedText } from '../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { LoadingModal } from '../../../LoadingModal/LoadingModal';
import { PageStatus } from './LinkAccounts.types';
import { FormValues, usePlaidAccountsModal, validation } from './usePlaidAccountsModal';
import { ErrorModal } from '../../../ErrorModal/ErrorModal';
import { useLinkAccountsMutation } from '../../../../api/useLinkAccountsMutation';
import { useTransferMoneyContext } from '../../../../hooks/useTransferMoneyContext';
import { Radio } from '../../../../../../CommonComponents/OPL/Radio/Radio';

interface Props {
  onClose(): void;
}

export const LinkAccountsModal: React.FC<Props> = ({ onClose }) => {
  const {
    data: { pageStatus, plaidLink, plaidData },
  } = usePlaidAccountsModal({ onClose });

  const {
    open,
    isScriptLoaded: isPlaidScripLoaded,
    isLoading: isPlaidLoading,
    queryError: plaidQueryError,
    mutationError: plaidMutationError,
  } = plaidLink;

  useEffect(() => {
    if (isPlaidScripLoaded) {
      open();
    }
    //  open changes on every render, hence is not added to the deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaidScripLoaded]);

  if (plaidQueryError) {
    return <ErrorModal onClose={onClose} onRetry={() => open()} />;
  }

  if (pageStatus === PageStatus.PlaidLoading) {
    return <FullPageLoading />;
  }

  if (pageStatus === PageStatus.PlaidModalVisible) {
    return null;
  }

  if (isPlaidLoading) {
    return <LoadingModal />;
  }

  if (plaidMutationError) {
    return <ErrorModal onClose={onClose} onRetry={() => open()} />;
  }

  if (!plaidData) {
    return null;
  }

  return <LinkAccountsModalContent plaidData={plaidData} onClose={onClose} />;
};

const LinkAccountsModalContent = ({ plaidData, onClose }) => {
  const { institutionName } = plaidData;

  const { clientId, managedAccountId } = useTransferMoneyContext();

  const formBag = useForm<FormValues>({
    defaultValues: {
      selectedAccountId: null,
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: linkAccounts,
    isLoading: isLinkingAccounts,
    error: linkAccountsError,
  } = useLinkAccountsMutation();

  const onSubmit = formBag.handleSubmit(async data => {
    const { selectedAccountId } = data;

    if (!plaidData || !selectedAccountId) {
      return;
    }

    try {
      await linkAccounts({
        clientId,
        managedAccountId,
        itemId: plaidData.itemId,
        accountIds: [selectedAccountId],
      });

      onClose();
    } catch (err) {
      console.error(err);
    }
  });

  return (
    <Modal isOpen onRequestClose={onClose} contentLabel="Link accounts">
      <ModalHeader variant="default">{institutionName}</ModalHeader>
      <ModalCloseButton onClick={onClose} disabled={isLinkingAccounts} />

      <Box as="form" onSubmit={onSubmit}>
        <Box px="24" pb="16">
          {linkAccountsError && (
            <Alert message={linkAccountsError.message} variant="error" mb="16" />
          )}

          {formBag.formState.errors.selectedAccountId && (
            <Alert
              message={formBag.formState.errors.selectedAccountId.message}
              variant="error"
              mb="16"
            />
          )}

          <Text mt="8">Select the account you would like to add</Text>
        </Box>

        <TableWrapper variant="embedded">
          <TableHeader>
            <TableRow noBorder px="24">
              <TableCell width="50%">
                <Text variant="smallCaps">Account Name</Text>
              </TableCell>

              <TableCell width="50%" justifyContent="flex-end">
                <Text variant="smallCaps">Account Id</Text>
              </TableCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            <Controller
              name="selectedAccountId"
              control={formBag.control}
              render={({ field: { value, name, onChange } }) => (
                <>
                  {plaidData.accounts
                    .filter(({ subtype }) => subtype === 'savings' || subtype === 'checking')
                    .map(({ id, name: accountName, mask }) => (
                      <TableRow key={id}>
                        <TableCell width="50%">
                          <Radio
                            key={id}
                            name={name}
                            checked={value === id}
                            onChange={() => onChange(id)}
                            helperText={institutionName}
                          >
                            {accountName}
                          </Radio>
                        </TableCell>

                        <TableCell width="50%" justifyContent="flex-end">
                          <TruncatedText>{`****${mask}`}</TruncatedText>
                        </TableCell>
                      </TableRow>
                    ))}
                </>
              )}
            />
          </TableBody>
        </TableWrapper>

        <ModalFooter>
          <Button onClick={onClose} variant="subtleInactive" autoFocus mr="12">
            Cancel
          </Button>

          <Button variant="primary" type="submit" isLoading={isLinkingAccounts}>
            Add
          </Button>
        </ModalFooter>
      </Box>
    </Modal>
  );
};
